import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckoutSummaryCard } from '@/components/customer/billing/CheckoutSummaryCard';
import { SubscriptionCheckoutForm } from '@/components/customer/billing/SubscriptionCheckoutForm';
import {
  useConfirmCustomerSubscriptionCheckout,
  useCreateCustomerSubscriptionCheckoutIntent,
  useCustomerSubscriptionCheckoutContext,
} from '@/hooks/customer/useCustomerSubscriptionCheckout';
import { useCustomerPlanManagement } from '@/hooks/customer/useCustomerPlanManagement';
import { useResumeSubscription } from '@/hooks/customer/useCustomerSubscriptionActions';
import { usePublicCheckout } from '@/hooks/public/usePublicCheckout';
import type { CustomerSubscriptionCheckoutIntent } from '@/types/customer-subscription-checkout';
import type { CustomerSubscriptionCheckoutIntentRequest } from '@/types/customer-subscription-checkout';
import type { CustomerSubscriptionCheckoutConfirmRequest } from '@/types/customer-subscription-checkout';
import { extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import { redirectToSuccessPage } from '@/lib/success-redirect';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';

export default function CustomerPlanManagementCheckoutPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const planManagementQuery = useCustomerPlanManagement();
  const createIntentMutation = useCreateCustomerSubscriptionCheckoutIntent();
  const confirmCheckoutMutation = useConfirmCustomerSubscriptionCheckout();
  const resumeMutation = useResumeSubscription();

  const [intent, setIntent] = useState<CustomerSubscriptionCheckoutIntent | null>(null);
  const [checkoutUnavailable, setCheckoutUnavailable] = useState<string | null>(null);
  const [autoActivationError, setAutoActivationError] = useState<string | null>(null);
  const [isAutoActivating, setIsAutoActivating] = useState(false);
  const [autoActivationRetryReady, setAutoActivationRetryReady] = useState(false);
  const autoActivationStartedRef = useRef(false);

  const action = searchParams.get('action');
  const planId = searchParams.get('plan_id');
  const priceId = searchParams.get('price_id');

  const checkoutContextQuery = useCustomerSubscriptionCheckoutContext(
    { planId, priceId },
    true,
  );
  const context = checkoutContextQuery.data ?? null;
  const productId = context?.planSummary.productId ?? null;
  const language = normalizeLanguageCode(i18n.language);
  const publicCheckoutQuery = usePublicCheckout(productId, Boolean(productId), {
    lang: language ?? null,
  });
  const checkoutPlanFeatures =
    planManagementQuery.data?.data.plans.find((plan) => plan.id === context?.planSummary.id)?.features ?? [];
  const checkoutFields = publicCheckoutQuery.data?.checkoutFields ?? [];
  const countryOptions = publicCheckoutQuery.data?.countryOptions ?? null;
  const currentSubscription = planManagementQuery.data?.data.currentSubscription ?? null;
  const hasUsableSubscription =
    currentSubscription?.displayStatus === 'active' || currentSubscription?.displayStatus === 'trial';

  const isReactivate = action === 'reactivate';
  const needsCheckoutForReactivate =
    isReactivate && (context?.requiresPayment === true || context?.stripeIntentType !== null);
  const selectedPlanId = planId ?? context?.planSummary.id ?? null;
  const selectedPriceId = priceId ?? null;
  const redirectToSubscriptionsSuccess = () => {
    redirectToSuccessPage('/customer/billing', {
      tab: 'subscriptions',
      checkout: 'success',
    });
  };

  const isNewSubscriptionState =
    context?.subscription?.id === null && (context?.subscription?.status ?? '').toLowerCase() === 'none';

  const shouldAutoActivateNoPayment = useMemo(
    () =>
      Boolean(context) &&
      !isReactivate &&
      !action &&
      isNewSubscriptionState &&
      context?.requiresPayment === false &&
      !hasUsableSubscription &&
      Boolean(selectedPlanId),
    [
      action,
      context,
      context?.requiresPayment,
      hasUsableSubscription,
      isNewSubscriptionState,
      isReactivate,
      selectedPlanId,
    ],
  );

  const runAutoActivation = useCallback(async () => {
    if (!selectedPlanId) {
      setAutoActivationError(t('customer.planManagement.checkout.errors.autoActivationMissingPlan'));
      setAutoActivationRetryReady(true);
      return;
    }

    setAutoActivationError(null);
    setAutoActivationRetryReady(false);
    setIsAutoActivating(true);

    try {
      const intentPayload: CustomerSubscriptionCheckoutIntentRequest = {
        plan_id: selectedPlanId,
        ...(selectedPriceId ? { price_id: selectedPriceId } : {}),
      };
      const createdIntent = await createIntentMutation.mutateAsync(intentPayload);
      setIntent(createdIntent);

      if (createdIntent.paymentRequired) {
        setAutoActivationError(t('customer.planManagement.checkout.errors.autoActivationRequiresPayment'));
        setAutoActivationRetryReady(true);
        return;
      }

      const confirmPayload: CustomerSubscriptionCheckoutConfirmRequest = {
        purchase_intent_id: createdIntent.purchaseIntentId,
      };

      const confirmOnce = () => confirmCheckoutMutation.mutateAsync(confirmPayload);

      const isPendingLike = (status: string | null | undefined) => {
        const normalized = (status ?? '').trim().toLowerCase();
        return normalized === 'pending' || normalized === 'incomplete';
      };

      let confirmResult = await confirmOnce();
      if (isPendingLike(confirmResult.subscriptionStatus)) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        confirmResult = await confirmOnce();
      }

      if (isPendingLike(confirmResult.subscriptionStatus)) {
        setAutoActivationError(t('customer.planManagement.checkout.errors.autoActivationPending'));
        setAutoActivationRetryReady(true);
        return;
      }

      redirectToSubscriptionsSuccess();
    } catch (error) {
      setAutoActivationError(
        extractJsonApiErrorMessage(error, t('customer.planManagement.checkout.errors.autoActivation')),
      );
      setAutoActivationRetryReady(true);
    } finally {
      setIsAutoActivating(false);
    }
  }, [
    confirmCheckoutMutation,
    createIntentMutation,
    navigate,
    selectedPlanId,
    selectedPriceId,
    t,
  ]);

  useEffect(() => {
    if (!context) {
      autoActivationStartedRef.current = false;
      return;
    }

    if (!shouldAutoActivateNoPayment) {
      autoActivationStartedRef.current = false;
      return;
    }

    if (autoActivationStartedRef.current) {
      return;
    }
    autoActivationStartedRef.current = true;
    void runAutoActivation();
  }, [context, runAutoActivation, shouldAutoActivateNoPayment]);

  if (checkoutContextQuery.isLoading) {
    return <Skeleton className="h-80 w-full" />;
  }

  if (checkoutContextQuery.isError || !context) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('customer.planManagement.checkout.errors.loadTitle')}</AlertTitle>
        <AlertDescription>
          {extractJsonApiErrorMessage(
            checkoutContextQuery.error,
            t('customer.planManagement.checkout.errors.loadDescription'),
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Some reactivation flows require checkout to (re)collect a payment method even if no immediate payment is due.
  // In that case, do not offer the resume shortcut.
  if (isReactivate && !context.requiresPayment && context.stripeIntentType === null) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('customer.planManagement.checkout.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('customer.planManagement.checkout.reactivateNoPayment')}
            </p>
          </div>
          <Button type="button" variant="outline" onClick={() => navigate('/customer/billing?tab=subscriptions')}>
            {t('customer.planManagement.checkout.backToPlans')}
          </Button>
        </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.25fr]">
        <CheckoutSummaryCard context={context} features={checkoutPlanFeatures} />
        <Card>
          <CardHeader>
            <CardTitle>{t('customer.planManagement.checkout.paymentTitle')}</CardTitle>
          </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTitle>{t('customer.planManagement.checkout.noPaymentTitle')}</AlertTitle>
                <AlertDescription>{t('customer.planManagement.checkout.noPaymentRequired')}</AlertDescription>
              </Alert>
              <Button
                type="button"
                onClick={async () => {
                  try {
                    await resumeMutation.mutateAsync();
                    redirectToSubscriptionsSuccess();
                  } catch (error) {
                    toast.error(
                      extractJsonApiErrorMessage(error, t('customer.planManagement.checkout.errors.confirm')),
                    );
                  }
                }}
                disabled={resumeMutation.isPending}
              >
                {resumeMutation.isPending
                  ? t('customer.planManagement.actions.processing')
                  : t('customer.planManagement.actions.resume')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (shouldAutoActivateNoPayment) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('customer.planManagement.checkout.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('customer.planManagement.checkout.autoActivationDescription')}
            </p>
          </div>
          <Button type="button" variant="outline" onClick={() => navigate('/customer/billing?tab=subscriptions')}>
            {t('customer.planManagement.checkout.backToPlans')}
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_1.25fr]">
          <CheckoutSummaryCard context={context} features={checkoutPlanFeatures} />
          <Card>
            <CardHeader>
              <CardTitle>{t('customer.planManagement.checkout.autoActivationTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert>
                <AlertTitle>{t('customer.planManagement.checkout.autoActivationTitle')}</AlertTitle>
                <AlertDescription>
                  {isAutoActivating
                    ? t('customer.planManagement.checkout.autoActivationLoading')
                    : t('customer.planManagement.checkout.autoActivationDescription')}
                </AlertDescription>
              </Alert>
              {autoActivationError ? <p className="text-sm text-destructive">{autoActivationError}</p> : null}
              {autoActivationRetryReady ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void runAutoActivation();
                  }}
                  disabled={isAutoActivating}
                >
                  {t('customer.planManagement.checkout.autoActivationRetry')}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleCreateIntent = async () => {
    try {
      setCheckoutUnavailable(null);
      setAutoActivationError(null);
      const payload: CustomerSubscriptionCheckoutIntentRequest = {};
      if (selectedPlanId) {
        payload.plan_id = selectedPlanId;
      }
      if (selectedPriceId) {
        payload.price_id = selectedPriceId;
      }
      const created = await createIntentMutation.mutateAsync(payload);
      setIntent(created);
    } catch (error) {
      const message = extractJsonApiErrorMessage(error, t('customer.planManagement.checkout.errors.intent'));
      // Backend may disallow checkout for certain statuses (e.g. cancelled reactivation without proper intent support).
      if (message.toLowerCase().includes('checkout is only available for expired or unpaid subscriptions')) {
        setCheckoutUnavailable(t('customer.planManagement.checkout.errors.unavailable'));
      }
      toast.error(message);
    }
  };

  const handleConfirmCheckout = async (
    purchaseIntentId: string,
    billingAddress?: Omit<CustomerSubscriptionCheckoutConfirmRequest, 'purchase_intent_id'>,
  ) => {
    try {
      await confirmCheckoutMutation.mutateAsync({
        purchase_intent_id: purchaseIntentId,
        ...(billingAddress ?? {}),
      });
      redirectToSubscriptionsSuccess();
    } catch (error) {
      toast.error(extractJsonApiErrorMessage(error, t('customer.planManagement.checkout.errors.confirm')));
    }
  };

  return (
    <div className="space-y-6">
      {checkoutUnavailable ? (
        <Alert variant="destructive">
          <AlertTitle>{t('customer.planManagement.checkout.errors.loadTitle')}</AlertTitle>
          <AlertDescription>{checkoutUnavailable}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('customer.planManagement.checkout.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('customer.planManagement.checkout.description')}</p>
          {action ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('customer.planManagement.checkout.pendingAction', { action })}
            </p>
          ) : null}
        </div>
        <Button type="button" variant="outline" onClick={() => navigate('/customer/billing?tab=subscriptions')}>
          {t('customer.planManagement.checkout.backToPlans')}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.25fr]">
        <CheckoutSummaryCard context={context} features={checkoutPlanFeatures} />

        <Card>
          <CardHeader>
            <CardTitle>{t('customer.planManagement.checkout.paymentTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <SubscriptionCheckoutForm
              context={context}
              intent={intent}
              onCreateIntent={handleCreateIntent}
              onConfirm={handleConfirmCheckout}
              forceCheckout={needsCheckoutForReactivate}
              checkoutFields={checkoutFields}
              countryOptions={countryOptions}
              isCreatingIntent={createIntentMutation.isPending}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
